import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Copy, FileUp, KeyRound, RotateCcw } from 'lucide-react';
import { Alert, Button, Field, Input, RadioGroup } from '../ui/index.js';
import { useTranslation } from '../../i18n/index.js';
import { useActivateInstallationSite } from '../../hooks/useApi.js';
const CLAIM_CODE = /^[A-HJ-NP-Z]{4}-[A-HJ-NP-Z]{4}$/;
/** The refusals with their own sentence under `activation.refusal.*`. */
const REFUSALS = new Set([
    'site_code_invalid', 'site_mode_mismatch', 'site_not_found', 'site_file_unavailable', 'backup_unavailable',
    'backup_member_unknown', 'backup_invalid', 'site_restore_not_empty', 'site_package_invalid',
    'site_package_claim_mismatch', 'site_certificate_invalid', 'site_certificate_claim_mismatch',
    'site_certificate_signer_mismatch', 'signing_key_import_failed', 'rate_limited', 'service_unavailable',
    'pending_token_not_permitted', 'not_authenticated', 'permission_denied',
]);
export function activationRefusalCode(error) {
    const code = error?.response?.data?.error;
    return typeof code === 'string' && code ? code : undefined;
}
export function SiteActivationForm({ onActivated }) {
    const { t } = useTranslation();
    const activate = useActivateInstallationSite();
    const fileRef = useRef(null);
    const [method, setMethod] = useState('setup');
    const [claimCode, setClaimCode] = useState('');
    const [code, setCode] = useState('');
    const [refusal, setRefusal] = useState(null);
    const claim = claimCode.trim().toUpperCase();
    const ready = CLAIM_CODE.test(claim) && /^\d{4}$/.test(code);
    const run = async (body) => {
        setRefusal(null);
        try {
            const result = await activate.mutateAsync(body);
            await onActivated(result);
        }
        catch (error) {
            setRefusal(activationRefusalCode(error) ?? 'unknown');
        }
        finally {
            setCode('');
        }
    };
    const submitCode = () => {
        if (!ready)
            return;
        void run(method === 'restore' ? { claimCode: claim, restoreCode: code } : { claimCode: claim, code });
    };
    const upload = async (file) => {
        let pkg;
        try {
            pkg = JSON.parse(await file.text());
        }
        catch {
            setRefusal('site_package_invalid');
            return;
        }
        await run(pkg);
    };
    const sentence = refusal
        ? t(`activation.refusal.${REFUSALS.has(refusal) ? refusal : 'unknown'}`)
        : null;
    return (_jsxs("div", { className: "flex flex-col gap-form-row", children: [_jsx(RadioGroup, { "aria-label": t('activation.method'), value: method, onChange: (value) => { setMethod(value); setRefusal(null); setCode(''); }, options: [
                    { value: 'setup', label: t('activation.setup'), description: t('activation.setupHint') },
                    { value: 'restore', label: t('activation.restore'), description: t('activation.restoreHint') },
                    { value: 'file', label: t('activation.file'), description: t('activation.fileHint') },
                ] }), method !== 'file' ? (_jsxs("form", { className: "flex flex-col gap-form-row", onSubmit: (event) => { event.preventDefault(); submitCode(); }, children: [_jsx(Field, { label: t('activation.claimCode'), hint: t('activation.claimCodeHint'), children: (control) => (_jsx(Input, { id: control.id, "aria-describedby": control['aria-describedby'], mono: true, value: claimCode, maxLength: 9, autoComplete: "off", spellCheck: false, placeholder: "XXXX-XXXX", onChange: (event) => setClaimCode(event.target.value.toUpperCase()) })) }), _jsx(Field, { label: method === 'restore' ? t('activation.restoreCode') : t('activation.setupCode'), hint: t('activation.codeHint'), children: (control) => (_jsx(Input, { id: control.id, "aria-describedby": control['aria-describedby'], type: "password", inputMode: "numeric", autoComplete: "off", maxLength: 4, className: "w-32", value: code, onChange: (event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4)) })) }), _jsx("div", { children: _jsxs(Button, { type: "submit", loading: activate.isPending, disabled: !ready, children: [method === 'restore' ? _jsx(RotateCcw, {}) : _jsx(KeyRound, {}), method === 'restore' ? t('activation.restoreAction') : t('activation.activateAction')] }) })] })) : (_jsxs("div", { children: [_jsxs(Button, { type: "button", variant: "outline", loading: activate.isPending, onClick: () => fileRef.current?.click(), children: [_jsx(FileUp, {}), t('activation.chooseFile')] }), _jsx("input", { ref: fileRef, hidden: true, type: "file", accept: "application/json,.json", "aria-label": t('activation.chooseFile'), onChange: (event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (file)
                                void upload(file);
                        } })] })), sentence && (_jsx(Alert, { tone: "error", title: sentence, action: (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => void navigator.clipboard?.writeText(JSON.stringify({ operation: method, code: refusal })), children: [_jsx(Copy, {}), t('activation.copyDetails')] })) }))] }));
}
//# sourceMappingURL=SiteActivationForm.js.map